import React, { useState, useEffect, Fragment } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { collection, getDocs, query, where, orderBy, deleteDoc, doc, addDoc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { Dialog, Transition } from '@headlessui/react';
import { getBookInfo } from '../utils/bibleData';
import StudyNoteEditor from './StudyNoteEditor';

// Helper function to get all family member IDs
const getFamilyMemberIds = async (familyIds) => {
  if (!familyIds || familyIds.length === 0) return [];
  
  const { collection, getDocs, query, where } = await import('firebase/firestore');
  const { db } = await import('../config/firebase');
  
  const allMemberIds = new Set();
  
  for (const familyId of familyIds) {
    try {
      const familyDoc = await getDocs(query(collection(db, 'families'), where('__name__', '==', familyId)));
      if (!familyDoc.empty) {
        const familyData = familyDoc.docs[0].data();
        if (familyData.memberIDs) {
          familyData.memberIDs.forEach(id => allMemberIds.add(id));
        }
      }
    } catch (error) {
      console.error('Error fetching family members:', error);
    }
  }
  
  return Array.from(allMemberIds);
};

const StudyNotesDrawer = ({ 
  isOpen, 
  onClose, 
  book, 
  chapter, 
  currentUser,
  familyMembers = []
}) => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all'); // 'all', 'written', 'audio'
  const [editingNote, setEditingNote] = useState(null);
  const [showNoteEditor, setShowNoteEditor] = useState(false);
  const [comments, setComments] = useState({}); // { noteId: [comments] }
  const [commentText, setCommentText] = useState({}); // { noteId: text }
  const [expandedComments, setExpandedComments] = useState({}); // { noteId: boolean }

  // Fetch notes for the chapter from all user's families
  const fetchChapterNotes = async () => {
    console.log('🔍 StudyNotesDrawer: Starting fetchChapterNotes...');
    
    if (!currentUser || !book || !chapter) {
      console.log('❌ StudyNotesDrawer: Missing required data:', {
        hasCurrentUser: !!currentUser,
        book,
        chapter
      });
      return;
    }

    console.log('📋 StudyNotesDrawer: Current user data:', {
      uid: currentUser.uid,
      displayName: currentUser.displayName,
      familyIds: currentUser.familyIds,
      fullUserObject: currentUser
    });

    console.log('📖 StudyNotesDrawer: Query parameters:', {
      book,
      chapter,
      familyIds: currentUser.familyIds || []
    });

    setLoading(true);
    setError('');

    try {
      console.log('🔍 StudyNotesDrawer: Building Firestore queries for each family...');
      
      // Get all family member IDs first
      const familyMemberIds = await getFamilyMemberIds(currentUser.familyIds);
      console.log(`🔍 StudyNotesDrawer: Found ${familyMemberIds.length} family members:`, familyMemberIds);
      
      // Query each family member's personal notes collection
      const memberQueries = familyMemberIds.map(memberId => 
        query(
          collection(db, `users/${memberId}/notes`),
          where('book', '==', book),
          where('chapter', '==', chapter),
          orderBy('createdAt', 'desc')
        )
      );
      
      // Execute all queries in parallel
      const snapshots = await Promise.all(memberQueries.map(q => getDocs(q)));
      
      console.log('📊 StudyNotesDrawer: Query results:', {
        totalMembers: snapshots.length,
        totalDocs: snapshots.reduce((sum, snap) => sum + snap.docs.length, 0)
      });
      
      // Combine results from all family members
      const allNotes = [];
      snapshots.forEach((snapshot, memberIndex) => {
        const memberId = familyMemberIds[memberIndex];
        console.log(`📊 StudyNotesDrawer: Member ${memberId} returned ${snapshot.docs.length} notes`);
        
        snapshot.docs.forEach(doc => {
          const data = doc.data();
          console.log('📄 StudyNotesDrawer: Processing note:', {
            id: doc.id,
            memberId,
            userId: data.userId,
            userName: data.userName,
            userDisplayName: data.userDisplayName,
            book: data.book,
            chapter: data.chapter,
            verse: data.verse,
            noteType: data.noteType,
            content: data.content?.substring(0, 50) + '...' || 'Audio note'
          });
          
          allNotes.push({
            id: doc.id,
            memberId, // Add memberId for reference
            ...data
          });
        });
      });
      
      console.log('📊 StudyNotesDrawer: Total notes found across all families:', allNotes.length);
      console.log('📊 StudyNotesDrawer: Notes by family:', allNotes.reduce((acc, note) => {
        acc[note.familyId] = (acc[note.familyId] || 0) + 1;
        return acc;
      }, {}));
      
      // Sort by creation date (newest first)
      allNotes.sort((a, b) => {
        const dateA = a.createdAt?.toDate?.() || a.createdAt || new Date(0);
        const dateB = b.createdAt?.toDate?.() || b.createdAt || new Date(0);
        return dateB - dateA;
      });

      console.log('✅ StudyNotesDrawer: Successfully loaded notes:', allNotes);
      setNotes(allNotes);
    } catch (error) {
      console.error('❌ StudyNotesDrawer: Error fetching chapter notes:', error);
      console.error('❌ StudyNotesDrawer: Error details:', {
        code: error.code,
        message: error.message,
        stack: error.stack
      });
      setError('Failed to load notes. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch notes when drawer opens
  useEffect(() => {
    console.log('🔄 StudyNotesDrawer: useEffect triggered:', {
      isOpen,
      book,
      chapter,
      hasCurrentUser: !!currentUser
    });
    
    if (isOpen) {
      console.log('📂 StudyNotesDrawer: Drawer opened, fetching notes...');
      fetchChapterNotes();
    } else {
      console.log('📂 StudyNotesDrawer: Drawer closed, clearing notes');
      setNotes([]);
      setError('');
    }
  }, [isOpen, book, chapter, currentUser]);

  // Filter notes based on selected filter
  const filteredNotes = notes.filter(note => {
    if (filter === 'all') return true;
    return note.noteType === filter;
  });

  // Handle note deletion
  const handleDeleteNote = async (noteId, memberId) => {
    if (!window.confirm('Are you sure you want to delete this note?')) return;

    try {
      await deleteDoc(doc(db, `users/${memberId}/notes`, noteId));
      setNotes(notes.filter(note => note.id !== noteId));
      console.log('📝 Note deleted:', noteId, 'from member:', memberId);
    } catch (error) {
      console.error('❌ Error deleting note:', error);
      setError('Failed to delete note. Please try again.');
    }
  };

  // Handle sharing note: only the author can share; mark it shareable and
  // copy a public link that includes the owner uid.
  const handleShareNote = async (noteId, ownerId) => {
    if (ownerId !== currentUser?.uid) {
      setError('You can only share your own notes.');
      return;
    }
    try {
      await updateDoc(doc(db, 'users', ownerId, 'notes', noteId), { isShared: true });
      setNotes(prev => prev.map(n => (n.id === noteId ? { ...n, isShared: true } : n)));

      const baseUrl = process.env.REACT_APP_BASE_URL || window.location.origin;
      const shareUrl = `${baseUrl}/notes/${ownerId}/${noteId}`;
      await navigator.clipboard.writeText(shareUrl);
      alert('Share link copied to clipboard!');
    } catch (error) {
      console.error('❌ Error copying share link:', error);
      setError('Failed to copy share link. Please try again.');
    }
  };

  // Handle note editing
  const handleEditNote = (note) => {
    setEditingNote(note);
    setShowNoteEditor(true);
  };

  // Handle note save (for editing)
  const handleNoteSave = () => {
    fetchChapterNotes(); // Refresh notes
    setEditingNote(null);
    setShowNoteEditor(false);
  };

  // Fetch comments for a specific note
  const fetchComments = async (noteId, memberId) => {
    try {
      const commentsQuery = query(
        collection(db, `users/${memberId}/notes/${noteId}/comments`),
        orderBy('createdAt', 'asc')
      );
      const snapshot = await getDocs(commentsQuery);
      const noteComments = snapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        .filter(comment => !comment.archived); // Filter out archived comments
      
      setComments(prev => ({
        ...prev,
        [noteId]: noteComments
      }));
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  };

  // Add a comment to a note
  const handleAddComment = async (noteId, memberId) => {
    const text = commentText[noteId]?.trim();
    if (!text || !currentUser) return;

    try {
      const commentData = {
        text: text,
        userId: currentUser.uid,
        userName: currentUser.displayName || currentUser.email || 'Family Member',
        userEmail: currentUser.email,
        createdAt: serverTimestamp()
      };

      await addDoc(collection(db, `users/${memberId}/notes/${noteId}/comments`), commentData);
      
      // Clear the comment input
      setCommentText(prev => ({
        ...prev,
        [noteId]: ''
      }));
      
      // Refresh comments for this note
      await fetchComments(noteId, memberId);
    } catch (error) {
      console.error('Error adding comment:', error);
      setError('Failed to add comment. Please try again.');
    }
  };

  // Toggle comment expansion
  const toggleComments = (noteId) => {
    setExpandedComments(prev => ({
      ...prev,
      [noteId]: !prev[noteId]
    }));
  };

  // Archive a comment
  const handleArchiveComment = async (noteId, memberId, commentId) => {
    if (!window.confirm('Are you sure you want to archive this comment? It will be hidden from view.')) return;

    try {
      await updateDoc(doc(db, `users/${memberId}/notes/${noteId}/comments`, commentId), {
        archived: true,
        archivedAt: serverTimestamp(),
        archivedBy: currentUser.uid
      });
      
      // Refresh comments for this note
      await fetchComments(noteId, memberId);
    } catch (error) {
      console.error('Error archiving comment:', error);
      setError('Failed to archive comment. Please try again.');
    }
  };

  // Get user display name
  const getUserDisplayName = (userId, userName, userDisplayName) => {
    if (userDisplayName) return userDisplayName;
    if (userName) return userName;
    return 'Family Member';
  };

  // Format book name for display
  const formatBookName = (bookId) => {
    return getBookInfo(bookId)?.name || bookId;
  };

  // Format relative time
  const formatRelativeTime = (timestamp) => {
    if (!timestamp) return 'Recently';
    
    const now = new Date();
    const noteTime = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const diffMs = now - noteTime;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return noteTime.toLocaleDateString();
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-in-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in-out duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="fixed inset-y-0 right-0 pl-10 max-w-full flex">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-300 sm:duration-500"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-300 sm:duration-500"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <Dialog.Panel className="w-screen max-w-md">
                  <div className="h-full flex flex-col bg-white dark:bg-[#0d1520] shadow-xl py-6 px-6">
                    <div className="flex items-center justify-between mb-4">
                      <Dialog.Title className="text-lg font-medium text-gray-900 dark:text-white">
                        Family Notes
                      </Dialog.Title>
                      <button
                        className="text-gray-400 dark:text-white/40 hover:text-gray-600 dark:hover:text-white"
                        onClick={onClose}
                      >
                        <span className="sr-only">Close panel</span>
                        <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>

                    <div className="mb-4">
                      <p className="text-sm text-gray-600 dark:text-white/50">{book} {chapter}</p>
                    </div>

                    {/* Filter Tabs */}
                    <div className="mb-4 border-b border-gray-200 dark:border-white/10">
                      <div className="flex">
                        {[
                          { key: 'all', label: 'All', count: notes.length },
                          { key: 'written', label: 'Written', count: notes.filter(n => n.noteType === 'written').length },
                          { key: 'audio', label: 'Audio', count: notes.filter(n => n.noteType === 'audio').length },
                          { key: 'image', label: 'Image', count: notes.filter(n => n.noteType === 'image').length }
                        ].map(tab => (
                          <button
                            key={tab.key}
                            onClick={() => setFilter(tab.key)}
                            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                              filter === tab.key
                                ? 'text-cerulean border-b-2 border-cerulean bg-cerulean bg-opacity-5'
                                : 'text-gray-600 dark:text-white/50 hover:text-gray-800 dark:hover:text-white/80'
                            }`}
                          >
                            {tab.label} ({tab.count})
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    <div className="space-y-4 flex-1 overflow-y-auto">
                      {/* Add Note Button */}
                      <button
                        onClick={() => setShowNoteEditor(true)}
                        className="w-full mb-4 p-3 bg-cerulean text-white rounded-lg hover:bg-space-cadet transition-colors font-medium flex items-center justify-center gap-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Add Note
                      </button>

                      {/* Error Message */}
                      {error && (
                        <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded-lg text-sm">
                          {error}
                        </div>
                      )}

                      {/* Loading State */}
                      {loading && (
                        <div className="text-center py-8">
                          <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-cerulean"></div>
                          <p className="text-gray-600 dark:text-white/50 mt-2">Loading notes...</p>
                        </div>
                      )}

                      {/* Notes List */}
                      {!loading && filteredNotes.length === 0 ? (
                        <div className="text-center py-8">
                          <div className="w-16 h-16 bg-gray-100 dark:bg-white/5 rounded-full flex items-center justify-center mx-auto mb-4">
                            <svg className="w-8 h-8 text-gray-400 dark:text-white/30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </div>
                          <p className="text-gray-600 dark:text-white/50">
                            {filter === 'all' ? 'No notes yet' : `No ${filter} notes yet`}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-white/35 mt-1">
                            Be the first to add a note for this chapter
                          </p>
                        </div>
                      ) : (
                        filteredNotes.map(note => (
                          <div key={note.id} className="bg-gray-50 dark:bg-white/5 p-4 rounded-lg">
                            {/* Note Header */}
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-space-cadet dark:text-white">
                                  {getUserDisplayName(note.userId, note.userName, note.userDisplayName)}
                                </span>
                                <span className="text-xs text-gray-500 dark:text-white/40">
                                  {formatRelativeTime(note.createdAt)}
                                </span>
                              </div>
                              <div className="flex items-center gap-1">
                                {note.userId === currentUser.uid && (
                                  <>
                                    <button
                                      onClick={() => handleShareNote(note.id, note.userId)}
                                      className="p-1 text-gray-400 dark:text-white/30 hover:text-aquamarine transition-colors"
                                      title="Share note"
                                    >
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                                      </svg>
                                    </button>
                                    <button
                                      onClick={() => handleEditNote(note)}
                                      className="p-1 text-gray-400 dark:text-white/30 hover:text-cerulean transition-colors"
                                      title="Edit note"
                                    >
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                      </svg>
                                    </button>
                                    <button
                                      onClick={() => handleDeleteNote(note.id, note.memberId)}
                                      className="p-1 text-gray-400 dark:text-white/30 hover:text-red-500 transition-colors"
                                      title="Delete note"
                                    >
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                      </svg>
                                    </button>
                                  </>
                                )}
                              </div>
                            </div>

                            {/* Verse Reference */}
                            <div className="mb-3">
                              <span className="inline-flex items-center text-xs bg-cerulean bg-opacity-15 text-cerulean px-3 py-1.5 rounded-full font-medium border border-cerulean border-opacity-20">
                                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                {note.verseRange 
                                  ? `${formatBookName(book)} ${chapter}:${note.verseRange.start}-${note.verseRange.end}`
                                  : note.verse 
                                    ? `${formatBookName(book)} ${chapter}:${note.verse}`
                                    : `${formatBookName(book)} ${chapter}` // Fallback to book and chapter
                                }
                              </span>
                            </div>

                            {/* Note Content */}
                            <div className="text-gray-800 dark:text-white/80">
                              {note.noteType === 'written' ? (
                                <p className="text-sm leading-relaxed">{note.content}</p>
                              ) : note.noteType === 'image' ? (
                                <div className="space-y-2">
                                  {note.imageUrl && (
                                    <a href={note.imageUrl} target="_blank" rel="noopener noreferrer">
                                      <img
                                        src={note.imageUrl}
                                        alt="Bible note"
                                        className="w-full rounded-lg object-contain max-h-72 border border-gray-200 dark:border-white/10"
                                      />
                                    </a>
                                  )}
                                  {note.content ? (
                                    <p className="text-sm text-gray-600 dark:text-white/50 italic">{note.content}</p>
                                  ) : null}
                                </div>
                              ) : (
                                <div className="space-y-2">
                                  <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-white/50">
                                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 14.5v-9l6 4.5-6 4.5z"/>
                                    </svg>
                                    Audio note ({note.audioDuration ? `${Math.round(note.audioDuration)}s` : 'Unknown duration'})
                                  </div>
                                  {note.audioUrl && (
                                    <audio controls className="w-full" playsInline>
                                      <source src={note.audioUrl} type={note.audioType || 'audio/mp4'} />
                                      Your browser does not support the audio element.
                                    </audio>
                                  )}
                                </div>
                              )}
                            </div>

                            {/* Comments Section */}
                            <div className="mt-4 border-t border-gray-200 dark:border-white/10 pt-3">
                              {/* Comments Toggle Button */}
                              <button
                                onClick={() => {
                                  toggleComments(note.id);
                                  if (!comments[note.id] && !expandedComments[note.id]) {
                                    fetchComments(note.id, note.memberId);
                                  }
                                }}
                                className="flex items-center gap-2 text-sm text-gray-600 dark:text-white/50 hover:text-gray-800 dark:hover:text-white/80 transition-colors mb-3"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                </svg>
                                {comments[note.id]?.length || 0} comments
                                <svg 
                                  className={`w-4 h-4 transition-transform ${expandedComments[note.id] ? 'rotate-180' : ''}`} 
                                  fill="none" 
                                  stroke="currentColor" 
                                  viewBox="0 0 24 24"
                                >
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                              </button>

                              {/* Comments List */}
                              {expandedComments[note.id] && (
                                <div className="space-y-3">
                                  {/* Existing Comments */}
                                  {comments[note.id]?.map(comment => (
                                    <div key={comment.id} className="flex gap-3 group">
                                      <div className="w-6 h-6 bg-gray-300 dark:bg-white/15 rounded-full flex-shrink-0 mt-1"></div>
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center justify-between mb-1">
                                          <div className="flex items-center gap-2">
                                            <span className="text-xs font-medium text-gray-700 dark:text-white/70">
                                              {comment.userName}
                                            </span>
                                            <span className="text-xs text-gray-500 dark:text-white/40">
                                              {formatRelativeTime(comment.createdAt)}
                                            </span>
                                          </div>
                                          {/* Archive button - only show for comment author or note author */}
                                          {(comment.userId === currentUser.uid || note.userId === currentUser.uid) && (
                                            <button
                                              onClick={() => handleArchiveComment(note.id, note.memberId, comment.id)}
                                              className="p-1 text-gray-400 dark:text-white/30 hover:text-red-500 active:text-red-600 transition-all duration-200 rounded"
                                              title="Archive comment"
                                            >
                                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                              </svg>
                                            </button>
                                          )}
                                        </div>
                                        <p className="text-sm text-gray-800 dark:text-white/80">{comment.text}</p>
                                      </div>
                                    </div>
                                  ))}

                                  {/* Add Comment Form */}
                                  <div className="flex gap-3">
                                    <div className="w-6 h-6 bg-cerulean rounded-full flex-shrink-0 mt-1"></div>
                                    <div className="flex-1 min-w-0">
                                      <div className="flex gap-2">
                                        <input
                                          type="text"
                                          placeholder="Add a comment..."
                                          value={commentText[note.id] || ''}
                                          onChange={(e) => setCommentText(prev => ({
                                            ...prev,
                                            [note.id]: e.target.value
                                          }))}
                                          onKeyPress={(e) => {
                                            if (e.key === 'Enter') {
                                              handleAddComment(note.id, note.memberId);
                                            }
                                          }}
                                          className="flex-1 px-3 py-2 text-sm bg-white dark:bg-white/5 text-gray-900 dark:text-white border border-gray-300 dark:border-white/15 rounded-lg focus:ring-2 focus:ring-cerulean focus:border-cerulean outline-none"
                                        />
                                        <button
                                          onClick={() => handleAddComment(note.id, note.memberId)}
                                          disabled={!commentText[note.id]?.trim()}
                                          className="px-3 py-2 bg-cerulean text-white text-sm rounded-lg hover:bg-space-cadet transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                          Post
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>

      {/* Study Note Editor Modal */}
      <StudyNoteEditor
        isOpen={showNoteEditor}
        onClose={() => {
          setShowNoteEditor(false);
          setEditingNote(null);
        }}
        note={editingNote}
        book={book}
        chapter={chapter}
        onSave={handleNoteSave}
      />
    </Transition.Root>
  );
};

export default StudyNotesDrawer;
